import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import type { RevisionCreatePayload } from '../../../services/quote.service';
import { REV_REASONS } from '../quoteOptions';
import DateTimePicker from '../../../components/DateTimePicker';

/** "YYYY-MM-DD" → Date (null when blank/unparseable). */
function parseIso(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((s ?? '').trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date → "YYYY-MM-DD" (the format createRevision expects). */
function fmtIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  open: boolean;
  quoteNumber: number;
  quoteType: string; // 'WAREHOUSE' | 'DIRECT'
  currentRevision: number;
  /** 'revision' = New Revision (same quote, rev reason required);
   *  'dupe' = Dupe Quote (new quotenumber, rev 1, no rev reason). */
  mode?: 'revision' | 'dupe';
  busy?: boolean;
  onSubmit: (payload: RevisionCreatePayload) => void;
  onCancel: () => void;
}

/**
 * V1 "New Revision" dialog (editrecord_quote.pl:4813-5014): pick a revision
 * reason and which pieces of the current revision to copy forward. Notes and
 * documents default OFF (V1 requires explicit opt-in for those).
 */
const NewRevisionDialog: React.FC<Props> = ({
  open, quoteNumber, quoteType, currentRevision, mode = 'revision', busy, onSubmit, onCancel,
}) => {
  const isWarehouse = quoteType === 'WAREHOUSE';
  const isDupe = mode === 'dupe';
  const [businesstype, setBusinesstype] = useState('RATE REVISION');
  // Dupe defaults Effective to today (like the create flow). Revision leaves it
  // blank so the backend's day-after-source-end-date default still applies.
  const [effectivedate, setEffectivedate] = useState(() => (isDupe ? fmtIso(new Date()) : ''));
  const [enddate, setEnddate] = useState('');
  const [flags, setFlags] = useState({
    copyCustomer: true,
    copySalesforceOpp: true,
    copyDray: true,
    copyWarehouse: true,
    copyLanes: true,
    copyHandling: true,
    copyCharges: true,
    copyProductRates: true,
    copyStopoffs: true,
    copyNotes: false,
    copyDocuments: false,
  });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  // End Date is optional for a new revision. A FUTURE end date is the normal,
  // expected case (the revision is active going forward), so it no longer prompts
  // a confirm.
  const submit = () => {
    const ed = enddate.trim();
    onSubmit({
      businesstype: isDupe ? '' : businesstype,
      ...(effectivedate.trim() ? { effectivedate: effectivedate.trim() } : {}),
      ...(ed ? { enddate: ed } : {}),
      ...flags,
    });
  };

  const flag = (key: keyof typeof flags, label: string) => (
    <label className="flex items-center gap-2 text-[0.75rem] text-slate-700 dark:text-slate-300 cursor-pointer">
      <input
        type="checkbox"
        checked={flags[key]}
        onChange={(e) => setFlags((f) => ({ ...f, [key]: e.target.checked }))}
      />
      {label}
    </label>
  );

  // Portal to <body> so the overlay escapes the host drawer panel's CSS
  // transform (which would otherwise be its containing block AND a lower
  // stacking context, confining + hiding it behind the drawer). At <body> its
  // fixed positioning is viewport-relative and z-[120] sits above the drawer
  // (z-[100]) — matching RevisionHistoryModal.
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-slate-900/40 flex items-center justify-center" role="dialog" aria-label={isDupe ? 'Dupe Quote' : 'New Revision'}>
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl border border-slate-200 w-[440px] max-h-[85vh] overflow-y-auto dark:border-gray-700"
      >
        <ModalHeader
          title={isDupe ? `Dupe Quote ${quoteNumber}-${currentRevision}` : `New Revision — Quote ${quoteNumber}`}
          onClose={busy ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 pt-3 text-[0.71875rem] text-slate-500 dark:text-slate-400">
          {isDupe
            ? 'Creates a brand-new quote (revision 1) copying the selected data; a "DUPED FROM" note is added automatically.'
            : `Copies from revision ${currentRevision}; the new revision number is assigned automatically.`}
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          {!isDupe && (
            <div>
              <div className="text-[0.65625rem] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Revision Reason</div>
              <select
                value={businesstype}
                onChange={(e) => setBusinesstype(e.target.value)}
                aria-label="Revision reason"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-[0.75rem] bg-white text-slate-900 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-600"
              >
                {REV_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[0.65625rem] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Effective</div>
              <DateTimePicker
                mode="date"
                selectionType="single"
                value={parseIso(effectivedate)}
                onChange={(v) => setEffectivedate(v instanceof Date ? fmtIso(v) : '')}
                labelType="none"
                ariaLabel="Effective date"
                placeholder="today"
                autoApply
                usePortal
                autoFlip
                className="w-full"
              />
            </div>
            <div>
              <div className="text-[0.65625rem] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">End Date</div>
              <DateTimePicker
                mode="date"
                selectionType="single"
                value={parseIso(enddate)}
                onChange={(v) => setEnddate(v instanceof Date ? fmtIso(v) : '')}
                labelType="none"
                ariaLabel="End date"
                placeholder="—"
                autoApply
                usePortal
                autoFlip
                className="w-full"
              />
            </div>
          </div>

          <div>
            <div className="text-[0.65625rem] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Copy From Current Revision</div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
              {flag('copyCustomer', 'Customer stats')}
              {isDupe && flag('copySalesforceOpp', 'Salesforce Opp')}
              {flag('copyDray', isWarehouse ? 'Dray / equipment' : 'Rates / equipment')}
              {isWarehouse && flag('copyWarehouse', 'Warehouse / storage')}
              {isWarehouse && flag('copyLanes', 'Delivery lanes')}
              {isWarehouse && flag('copyHandling', 'Handling items')}
              {flag('copyCharges', 'Tariff charges')}
              {isWarehouse && flag('copyProductRates', 'Product rates')}
              {flag('copyStopoffs', 'Stopoffs')}
              {flag('copyNotes', 'Notes')}
              {flag('copyDocuments', 'Documents')}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-[0.75rem] text-slate-600 bg-panel border border-slate-300 rounded hover:bg-slate-50 dark:text-slate-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || (!isDupe && !businesstype)}
            onClick={submit}
            className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded disabled:opacity-40 hover:bg-blue-700"
          >
            {busy ? 'Creating…' : isDupe ? 'Dupe Quote' : 'Create Revision'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NewRevisionDialog;
